import { createWorker, PSM } from "tesseract.js";
import { layoutDocument } from "./pdfLayout";
import { readPageContent, type PageContent } from "./pdfPageContent";
import { extractImagesFromPage, renderPageToJpegBlob } from "./utils";
import { preprocessForOcr } from "./ocrImage";
import { ocrWordsToItems, type OcrWordsPage } from "./ocrWords";
import {
  buildWorkbook,
  joinContinuedTables,
  planSheets,
  tablesFromLayout,
  workbookToBlob,
  DEFAULT_EXCEL_OPTIONS,
  type ExcelOptions,
  type PreviewSheet,
} from "./pdfTables";

type TesseractWorker = Awaited<ReturnType<typeof createWorker>>;

export class ExcelConversionCancelled extends Error {
  constructor() {
    super("cancelled");
  }
}

export interface ExcelHooks {
  /** False once this run was stopped, reset or replaced. Checked after every await. */
  isCurrent: () => boolean;
  status: (message: string) => void;
  progress: (fraction: number) => void;
  /** Lets the caller terminate the OCR engine the moment the run is stopped. */
  registerWorker: (worker: TesseractWorker | null) => void;
}

export interface ExcelRunOptions extends ExcelOptions {
  /** Read pages that have no text layer with OCR (needs the language data, downloaded on first use). */
  ocr: boolean;
  ocrLanguage: string;
}

export const DEFAULT_RUN_OPTIONS: ExcelRunOptions = { ...DEFAULT_EXCEL_OPTIONS, ocr: true, ocrLanguage: "eng" };

export interface ExcelSummary {
  pages: number;
  tables: number;
  sheets: number;
  rows: number;
  numericCells: number;
  dateCells: number;
  ocrPages: number;
  /** Pages with no text that were not read (OCR off or found nothing). */
  unreadPages: number;
  /** Tables that continue over a page break and were joined: [firstPage, lastPage]. */
  joined: [number, number][];
  notes: string[];
}

export type ExcelOutcome =
  | { kind: "ok"; blob: Blob; preview: PreviewSheet[]; summary: ExcelSummary }
  | { kind: "none"; summary: ExcelSummary; reason: "no-text" | "no-tables" };

const MIN_OCR_WORDS = 3;

/**
 * Finds the tables in a PDF and writes them to a workbook. Only tables are written (not the prose
 * around them); a table that continues over a page break is joined; pages that are scans are read
 * with OCR first. Throws ExcelConversionCancelled when the run is no longer current.
 */
export async function convertPdfToExcelDocument(pdf: any, options: ExcelRunOptions, hooks: ExcelHooks): Promise<ExcelOutcome> {
  const alive = () => {
    if (!hooks.isCurrent()) throw new ExcelConversionCancelled();
  };
  const numPages: number = pdf.numPages;
  const contents: PageContent[] = [];
  const ocrPages = new Set<number>();
  let unreadPages = 0;
  let worker: TesseractWorker | null = null;
  /** The page being read with OCR, so the engine's own progress can move the bar inside a page. */
  let ocrPage = 0;

  try {
    for (let i = 0; i < numPages; i++) {
      hooks.status(`Reading page ${i + 1} of ${numPages}…`);
      hooks.progress(i / numPages);
      const page = await pdf.getPage(i + 1);
      alive();
      const content = await readPageContent(page);
      alive();
      if (content.hasText || !options.ocr) {
        if (!content.hasText) unreadPages++;
        contents.push(content);
        continue;
      }

      // A scanned page: read it with OCR, then treat the words like a text layer.
      if (!worker) {
        hooks.status("Starting OCR (first use downloads the language data)…");
        try {
          worker = await createWorker(options.ocrLanguage, 1, {
            logger: (m: { status?: string; progress?: number }) => {
              if (!hooks.isCurrent() || typeof m.progress !== "number") return;
              if (m.status === "recognizing text") {
                hooks.progress((ocrPage + m.progress) / numPages);
                hooks.status(`Reading scanned page ${ocrPage + 1} of ${numPages} with OCR… ${Math.round(m.progress * 100)}%`);
              } else if (/loading|initializ/i.test(m.status ?? "")) {
                hooks.status(`Starting OCR, preparing the language data… ${Math.round(m.progress * 100)}%`);
              }
            },
          });
        } catch (err) {
          if (!hooks.isCurrent()) throw new ExcelConversionCancelled();
          console.error("OCR engine failed to start", err);
          throw new Error("The OCR engine couldn't start. It downloads its language data the first time it is used, so it needs an internet connection; check yours and try again.");
        }
        if (!hooks.isCurrent()) {
          await worker.terminate().catch(() => undefined);
          worker = null;
          throw new ExcelConversionCancelled();
        }
        hooks.registerWorker(worker);
        await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO });
        alive();
      }
      ocrPage = i;
      hooks.status(`Reading scanned page ${i + 1} of ${numPages} with OCR…`);
      const pageArea = content.widthPt * content.heightPt;
      const found = await extractImagesFromPage(page).catch(() => ({ images: [], failed: 0 }));
      alive();
      const scan = found.images
        .filter((b) => b.position.widthPt * b.position.heightPt >= 0.5 * pageArea && !b.fromRender)
        .sort((a, b) => b.position.widthPt * b.position.heightPt - a.position.widthPt * a.position.heightPt)[0];
      const raw = scan ? scan.blob : await renderPageToJpegBlob(page, 3, 0.92);
      alive();
      const prepared = await preprocessForOcr(raw);
      alive();
      const bitmap = await createImageBitmap(prepared);
      const { width: imgW, height: imgH } = bitmap;
      bitmap.close?.();
      alive();
      const data = (await worker.recognize(prepared, {}, { text: true, blocks: true })).data as unknown as OcrWordsPage;
      alive();
      const items = ocrWordsToItems(data, content.widthPt / imgW, content.heightPt / imgH);
      if (items.length >= MIN_OCR_WORDS) {
        ocrPages.add(i + 1);
        contents.push({ ...content, items, hasText: true });
      } else {
        unreadPages++;
        contents.push(content);
      }
    }

    hooks.status("Finding tables…");
    hooks.progress(0.9);
    const layout = layoutDocument(contents);
    let tables = tablesFromLayout(layout, ocrPages);
    const joined: [number, number][] = [];
    if (options.joinContinued && options.sheetMode !== "page") {
      const before = tables.length;
      tables = joinContinuedTables(tables);
      if (tables.length < before) for (const t of tables) if (t.pages.length > 1) joined.push([t.pages[0], t.pages[t.pages.length - 1]]);
    }

    const summaryBase: ExcelSummary = {
      pages: numPages,
      tables: tables.length,
      sheets: 0,
      rows: 0,
      numericCells: 0,
      dateCells: 0,
      ocrPages: ocrPages.size,
      unreadPages,
      joined,
      notes: [],
    };
    if (tables.length === 0) {
      return { kind: "none", reason: contents.every((c) => !c.hasText) ? "no-text" : "no-tables", summary: summaryBase };
    }

    hooks.status("Building the workbook…");
    hooks.progress(0.97);
    const plans = planSheets(tables, options.sheetMode);
    const built = buildWorkbook(plans, options);
    const notes = [...built.notes];
    if (ocrPages.size > 0) notes.push(`${ocrPages.size === 1 ? "1 page was a scan and was" : `${ocrPages.size} pages were scans and were`} read with OCR, so check numbers against the original.`);
    if (unreadPages > 0 && options.ocr) notes.push(`${unreadPages} ${unreadPages === 1 ? "page" : "pages"} had no readable text and ${unreadPages === 1 ? "was" : "were"} skipped.`);
    if (unreadPages > 0 && !options.ocr) notes.push(`${unreadPages} ${unreadPages === 1 ? "page has" : "pages have"} no text layer (a scan) and ${unreadPages === 1 ? "was" : "were"} skipped; turn on scanned-page reading to include ${unreadPages === 1 ? "it" : "them"}.`);
    return {
      kind: "ok",
      blob: workbookToBlob(built.workbook),
      preview: built.preview,
      summary: { ...summaryBase, sheets: built.preview.length, rows: built.rowCount, numericCells: built.numericCells, dateCells: built.dateCells, notes },
    };
  } finally {
    if (worker) {
      hooks.registerWorker(null);
      try {
        await (worker as TesseractWorker).terminate();
      } catch {
        /* already gone */
      }
    }
  }
}
